import { useEffect, useRef } from 'react';

import type { IMoveParams, IScrollParams } from '../models';

export const OPTION_HEIGHT = 40;
export const OPTIONS_PER_VIEW = 4;

const getOffsetTop = (highlightedItem: number) => (highlightedItem + 1) * OPTION_HEIGHT;

const onOpenSelect = (shift: number) => shift + OPTION_HEIGHT;

const onMoveOptionDown = ({ highlightedItem, scrollTop, shift }: IMoveParams) => {
    const next = shift - (getOffsetTop(highlightedItem) - OPTION_HEIGHT * (highlightedItem + 1));

    if (next > scrollTop) {
        return next;
    }

    return scrollTop;
};

const onMoveOptionTop = ({ highlightedItem, scrollTop, shift }: IMoveParams) => {
    const next =
        shift -
        (getOffsetTop(highlightedItem) - OPTION_HEIGHT * (highlightedItem + OPTIONS_PER_VIEW));

    if (next < scrollTop) {
        return next;
    }

    return scrollTop;
};

const onMoveOption = ({
    highlightedItem,
    prevIndex,
    scrollTop,
    shift,
}: IMoveParams & { prevIndex: number }) => {
    if (highlightedItem < prevIndex) {
        return onMoveOptionTop({ highlightedItem, scrollTop, shift });
    }

    return onMoveOptionDown({ highlightedItem, scrollTop, shift });
};

const calculateScroll = ({
    highlightedItem,
    prevIndex,
    scrollTop,
    shift,
}: IMoveParams & { prevIndex: number }) => {
    if (Number.isFinite(prevIndex)) {
        return onMoveOption({ highlightedItem, prevIndex, scrollTop, shift });
    }

    return onOpenSelect(shift);
};

export const getScroll = ({ highlightedItem, prevIndex, scrollTop }: IScrollParams) =>
    calculateScroll({
        highlightedItem,
        prevIndex,
        scrollTop,
        shift: getOffsetTop(highlightedItem) - OPTION_HEIGHT * OPTIONS_PER_VIEW,
    });

export const usePrevious = (value: number) => {
    const ref = useRef<number>();

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current ?? value;
};
